/**
 * WineBoxDrawer.js — Persistent Build Wine Box drawer (before-input slot).
 *
 * Parity with old #wine-box-drawer mounted above the chat footer.
 */

import { on, emit } from "../../../core/state.js";
import {
  BUILD_BOX_TIERS,
  getBuildBoxState,
  refreshWineBoxDrawer,
} from "../features/wine-box.js";

function frequencyHTML(active) {
  return `
    <div class="wine-box-frequency" role="group" aria-label="Delivery frequency">
      <button type="button" class="wine-box-frequency-btn${active === "monthly" ? " is-active" : ""}" data-wine-box-frequency="monthly" aria-pressed="${active === "monthly"}">Monthly</button>
      <button type="button" class="wine-box-frequency-btn${active === "oneoff" ? " is-active" : ""}" data-wine-box-frequency="oneoff" aria-pressed="${active === "oneoff"}">One-off</button>
    </div>
  `;
}

export default class WineBoxDrawer {
  render({ shadow, panelEl }) {
    this._shadow = shadow;
    this._panelEl = panelEl;

    const root = document.createElement("div");
    root.className = "ws-wine-box-drawer-root";

    const toastHost = document.createElement("div");
    toastHost.id = "wine-box-toast-host";
    toastHost.className = "wine-box-toast-host";
    toastHost.setAttribute("aria-live", "polite");

    const drawer = document.createElement("div");
    drawer.id = "wine-box-drawer";
    drawer.className = "wine-box-drawer";
    drawer.setAttribute("data-wine-box-drawer", "");
    drawer.hidden = true;

    const tierButtons = [3, 6, 12]
      .map((tier) => {
        const config = BUILD_BOX_TIERS[tier];
        return `<button type="button" class="wine-box-tier-btn" data-wine-box-tier="${tier}" aria-pressed="false">
          ${config.label} · <span class="wine-box-tier-save">${config.saveLabel}</span>
        </button>`;
      })
      .join("");

    drawer.innerHTML = `
      <div class="wine-box-tier-row">
        <div class="wine-box-subscribe-banner">
          <button type="button" class="wine-box-banner-close" data-wine-box-banner-close aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0.5" y="0.5" width="19" height="19" rx="9.5" stroke="black"/>
              <path d="M10.549 10L14 13.451L13.451 14L10 10.549L6.54902 14L6 13.451L9.45098 10L6 6.54902L6.54902 6L10 9.45098L13.451 6L14 6.54902L10.549 10Z" fill="black"/>
            </svg>
          </button>
          <p class="wine-box-banner-text">Subscribe &amp; save <strong>up to 15%</strong></p>
        </div>
        <div class="wine-box-tiers" role="tablist" aria-label="Bottle count" data-wine-box-tiers>
          ${tierButtons}
        </div>
        ${frequencyHTML("monthly")}
      </div>
      <div class="wine-box-selection">
        <div class="wine-box-selection-header">
          <span>Your Selected Wines (<span data-wine-box-count>0</span>) :</span>
          <button type="button" class="wine-box-expand-btn" aria-label="Expand selected wines">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 10.5L2.5 6H11.5L7 10.5Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
        <div class="wine-box-slots" data-wine-box-slots></div>
      </div>
      <div class="wine-box-footer">
        <div class="wine-box-footer-main">
          <div class="wine-box-subtotal-row">
            <span class="wine-box-subtotal-label">Subtotal</span>
            <span class="wine-box-subtotal-prices" data-wine-box-subtotal-prices>
              <span class="wine-box-subtotal-now" data-wine-box-subtotal-now>£0.00</span>
            </span>
          </div>
          <p class="wine-box-shipping-msg" data-wine-box-shipping></p>
        </div>
        <button type="button" class="wine-box-continue-btn" data-wine-box-continue disabled>Continue</button>
      </div>
    `;

    drawer.addEventListener("click", (e) => {
      const tierBtn = e.target.closest("[data-wine-box-tier]");
      if (tierBtn) {
        emit("ws-build-box-set-tier", {
          tier: parseInt(tierBtn.dataset.wineBoxTier, 10),
        });
        return;
      }
      const freqBtn = e.target.closest("[data-wine-box-frequency]");
      if (freqBtn) {
        emit("ws-build-box-set-frequency", {
          frequency: freqBtn.dataset.wineBoxFrequency,
        });
        return;
      }
      if (e.target.closest("[data-wine-box-continue]")) {
        // Show Your Cart in-chat only — do not syncCartWithWebsite/reload
        // (that closes the agent; old main.js Continue never reloaded).
        emit("ws-build-box-continue");
      }
    });

    // Toast host sits near the top of the panel (absolute).
    if (panelEl) {
      panelEl.appendChild(toastHost);
    }

    root.appendChild(drawer);

    const syncVisibility = () => {
      const state = getBuildBoxState();
      const show = !!(state.active && state.drawerVisible);
      drawer.hidden = !show;
      drawer.classList.toggle("is-visible", show);
      drawer.classList.toggle("is-oneoff", state.frequency === "oneoff");
      panelEl?.classList.toggle("has-wine-box-drawer", show);
      if (show) refreshWineBoxDrawer(this._shadow);
    };

    on("ws-build-box-changed", syncVisibility);
    syncVisibility();

    return root;
  }
}
